/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Component, Input, OnChanges} from '@angular/core';
import template from './sorting-header.component.html';
import {ADMIN_LIST_IMPORTS} from '../admin-list-imports';

import {SignalAdminListColumn} from '../../controller/signal-admin-resource-list-state';

export type AdnSortingHeaderParam = string | SignalAdminListColumn;

@Component({
  standalone: true,
  imports: ADMIN_LIST_IMPORTS,
  selector: 'tr[adn-list-sort-header-row]',
  template,
  host: {
    role: 'row',
    class: 'listRowHeader'
  }
})
export class AdnSortingHeaderComponent implements OnChanges {
  @Input() list: any;
  /** Legacy callers pass string or param objects via params. */
  @Input() params: AdnSortingHeaderParam[] = [];
  /** List pages pass normalised AdminListColumn arrays. */
  @Input() columns: SignalAdminListColumn[];
  @Input() hideActions: boolean | string;
  @Input() extraField: boolean | string;
  @Input() translationPrefix = 'label';

  hideActionsValue = false;

  ngOnChanges(): void {
    this.hideActionsValue = this.coerceBoolean(this.hideActions);
  }

  get headerColumns(): AdnSortingHeaderParam[] {
    return this.columns?.length ? this.columns : this.params;
  }

  columnTranslateKey(paramValue: AdnSortingHeaderParam): string {
    if (this.columns?.length) {
      return this.columnLabel(paramValue);
    }
    return this.translationPrefix + '.' + this.labelKey(paramValue);
  }

  sortParam(paramValue: AdnSortingHeaderParam): string {
    if (typeof paramValue === 'string') {
      return paramValue;
    }
    return paramValue.param ?? '';
  }

  labelKey(paramValue: AdnSortingHeaderParam): string {
    if (typeof paramValue === 'string') {
      return paramValue;
    }
    return paramValue.label || paramValue.param || '';
  }

  labelInstant(paramValue: AdnSortingHeaderParam): string | undefined {
    if (typeof paramValue === 'string') {
      return undefined;
    }
    return paramValue.labelInstant;
  }

  isNumeric(paramValue: AdnSortingHeaderParam): boolean {
    return typeof paramValue !== 'string' && !!paramValue.numeric;
  }

  isSortable(paramValue: AdnSortingHeaderParam): boolean {
    if (typeof paramValue !== 'string' && paramValue.sortable === false) {
      return false;
    }
    return !!this.sortParam(paramValue).length;
  }

  columnLabel(paramValue: AdnSortingHeaderParam): string {
    if (typeof paramValue === 'string') {
      return 'label.' + paramValue;
    }
    const key = paramValue.label || paramValue.param;
    return key ? 'label.' + key : '';
  }

  sort(paramValue: AdnSortingHeaderParam, event?: Event): void {
    event?.preventDefault();
    if (!this.list || typeof this.list.makeSortQuery !== 'function') {
      return;
    }
    const param = this.sortParam(paramValue);
    if (!param.length) {
      return;
    }
    this.list.makeSortQuery(param, this.isNumeric(paramValue) || undefined);
  }

  get sorting(): boolean {
    return this.list?.listState?.sorting() ?? this.list?.sortQuery?.$resolved === false;
  }

  private coerceBoolean(value: boolean | string | undefined): boolean {
    return value === '' || value === true || value === 'true';
  }
}
